<template>
  <b-navbar fixed="top">
    <!--
      On a phone the logo gets what room the balance, the softphone and the menus leave: the whole
      logo, the X on its own, or nothing (see the header's global styles). The X is an <img>, not an
      inline SVG like the logo: the SVG loader shortens both files' ids to the same letters, and
      each would paint with the other's gradients.
    -->
    <b-navbar-brand
      class="header__logo mr-1 mr-sm-2 mr-md-5 pl-sm-2"
      href="#"
      aria-label="RingbaX"
    >
      <RingbaXLogoDark class="header__logo-wordmark" />
      <img
        class="header__logo-mark"
        :src="logoMarkUrl"
        alt=""
      >
    </b-navbar-brand>

    <button
      v-if="!hideHamburger"
      class="btn btn-link btn-icon bg-transparent header__sidebar-toggle"
      type="button"
      aria-label="Toggle navigation"
      aria-controls="sidebar"
      data-cy="button-sidebar-toggle"
      @click="onSidebarToggle"
    >
      <font-awesome-icon :icon="['fas', 'bars']" />
    </button>

    <AccountSummaryHeader v-if="!hideSummary" />

    <b-navbar-nav class="ml-auto align-items-center flex-nowrap">
      <!--
        The softphone lives here rather than in a window of its own. An agent works in the
        dashboard, so the thing that rings them belongs in the chrome that is always on screen -
        and one tab's header is a great deal harder to lose than a popup behind the browser.
      -->
      <SoftphoneHeaderWidget v-if="isSoftphoneAvailable" />

      <b-nav-item
        v-if="showSupportChat"
        class="header__support mr-md-3"
        :link-attrs="{ 'aria-label': 'Support' }"
        @click="toggleSupportChat"
      >
        <div class="d-flex flex-column align-items-center header__nav-item-with-alert">
          <font-awesome-icon
            :icon="['fa', 'comments']"
            class="header__nav-item-icon"
          />
          <span class="header__nav-item-text d-none d-md-block">Support</span>

          <span
            v-if="unreadChatCount"
            class="header__nav-item-alert-icon"
          >
            {{ unreadChatCount > 10 ? '+10' : unreadChatCount }}
          </span>
        </div>
      </b-nav-item>

      <b-nav-item
        v-if="showLogout"
        class="mr-3"
        @click="logoutAndRedirect"
      >
        Logout
      </b-nav-item>

      <b-nav-item-dropdown
        right
        class="header__user-menu"
        menu-class="account-switcher-menu"
        data-cy="button-user-menu"
        @hide="handleOnSwitchHide"
        @hidden="onDropdownHidden"
        @shown="onDropdownShown"
      >
        <!-- A name does not fit a phone's header, so below md it is an icon that still reads as the name. -->
        <template #button-content>
          <font-awesome-icon
            :icon="['fas', 'user-circle']"
            class="header__user-icon d-md-none"
          />
          <span class="header__user-name">
            {{ userDetails.name }}
          </span>
        </template>

        <div class="px-3 mb-1 font-weight-bold">
          Switch Account
        </div>

        <template v-if="totalAccountCount > 10">
          <b-dropdown-form
            class="px-2 pb-2"
            @submit.prevent
          >
            <b-form-input
              ref="accountSearch"
              v-model="searchQuery"
              size="sm"
              placeholder="Search accounts..."
              autocomplete="off"
              @keydown.stop
            />
          </b-dropdown-form>

          <b-dropdown-divider class="mt-0" />
        </template>

        <div class="account-switcher-list">
          <b-dropdown-item
            v-for="account in filteredAccounts"
            :key="account.id"
            href="#"
            class="account-item"
            @click="switchAccount(account.id)"
          >
            <font-awesome-icon
              v-if="!isLoading || account.id !== newAccountId"
              :class="account.id === accountId ? 'visible' : 'invisible'"
              :icon="['fas', 'check']"
              class="mr-2"
            />
            <b-spinner
              v-if="isLoading && account.id === newAccountId"
              class="mr-2"
            />
            <span :class="{ 'font-weight-bold': account.id === accountId }">{{ account.name }}</span>
            <i class="font-weight-light"><small> ({{ account.type }})</small></i>
          </b-dropdown-item>

          <b-dropdown-text v-if="filteredAccounts.length === 0">
            No accounts found.
          </b-dropdown-text>
        </div>
      </b-nav-item-dropdown>
    </b-navbar-nav>
  </b-navbar>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import RingbaXLogoDark from '@/assets/img/ringbax-logo-dark-theme.svg'
import AccountSummaryHeader from '@/components/billing/AccountSummaryHeader.vue'
import SoftphoneHeaderWidget from '@/components/softphone/SoftphoneHeaderWidget.vue'
import crispChatMixin from '@/mixins/crisp-chat.mixin'
import logoutMixin from '@/mixins/logout.mixin'
import { showErrorMessage } from '@/notification-utils'
import { DRAWER_LAYOUT_QUERY } from '@/utils/layoutTokens'

// The logo's X on its own, served from public/ as a file (see the template).
const LOGO_MARK_URL = `${process.env.BASE_URL}img/ringbax-mark.svg`

export default {
  name: 'TheHeader',

  components: {
    RingbaXLogoDark,
    AccountSummaryHeader,
    SoftphoneHeaderWidget
  },

  mixins: [crispChatMixin, logoutMixin],

  props: {
    showLogout: {
      type: Boolean,
      default: false
    },

    hideSummary: {
      type: Boolean,
      default: false
    },

    hideHamburger: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isLoading: false,
      newAccountId: '',
      searchQuery: '',
      logoMarkUrl: LOGO_MARK_URL
    }
  },

  computed: {
    ...mapState('auth', [
      'userDetails'
    ]),

    ...mapGetters('auth', [
      'allUserAccounts',
      'accountId'
    ]),

    // The single softphone gate: the global SOFTPHONE_GOES_LIVE setting AND the per-user agent
    // flag. LayoutDashboard fetches the setting, so this is false for a moment on a cold load and
    // the widget appears once it lands.
    ...mapGetters('softphone', ['isSoftphoneAvailable']),

    accountList() {
      if (!this.allUserAccounts) return []
      return Array.isArray(this.allUserAccounts)
        ? this.allUserAccounts
        : Object.values(this.allUserAccounts)
    },

    totalAccountCount() {
      return this.accountList.length
    },

    filteredAccounts() {
      const query = this.searchQuery.toLowerCase().trim()
      return query
        ? this.accountList.filter(a => a.name.toLowerCase().includes(query))
        : this.accountList
    }
  },

  async created () {
    await this.fetchUserAccounts()
  },

  methods: {
    ...mapActions(['toggleSidebar']),
    
    ...mapActions('auth', [
      'fetchUserAccounts',
      'switchCurrentAccount'
    ]),

    // On a phone, and on a tablet upright, the sidebar is an off-canvas drawer rather than a column
    // (see TheSidebar), so this button opens and closes it instead of collapsing it to icons.
    onSidebarToggle() {
      if (window.matchMedia?.(DRAWER_LAYOUT_QUERY).matches) {
        this.$root.$emit('bv::toggle::collapse', 'sidebar')

        return
      }

      this.toggleSidebar()
    },

    async switchAccount(accountId) {
      if (accountId === this.accountId) {
        return
      }
      
      try {
        this.isLoading = true

        this.newAccountId = accountId

        await this.switchCurrentAccount(accountId)

        window.location.reload()
      } catch {
        showErrorMessage('Unable to switch account, please contact support.')
      } finally {
        this.isLoading = false
        this.newAccountId = ''
      }
    },

    handleOnSwitchHide(ev) {
      if (this.isLoading) {
        ev.preventDefault()
      }
    },

    onDropdownHidden() {
      this.searchQuery = ''
    },

    onDropdownShown() {
      this.$nextTick(() => {
        this.$refs.accountSearch && this.$refs.accountSearch.focus()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.header__logo {
  width: 125px;
}

.header__sidebar-toggle {
  color: var(--component-sidebar__toggle-font-color);
}

.header__nav-item-text {
  font-size: 0.8rem;
}

.header__nav-item-icon {
  font-size: 1rem;
}

.header__nav-item-with-alert {
  position: relative;
}

.header__nav-item-alert-icon {
  position: absolute;
  right: 8px;
  top: -2px;
  font-size: 7px;
  color: #fff;
  font-weight: 200;
  text-align: center;
  line-height: 14px;
  width: 15px;
  height: 14px;
  background: red;
  border-radius: 50%;
}

.account-item {
  font-size: 0.85rem;
}

::v-deep .account-switcher-menu {
  min-width: 280px;

  .form-control {
    background-color: var(--component-custom-input-bg);
    color: var(--component-custom-input-font-color);
    border-color: var(--component-custom-input-border-color);

    &::placeholder {
      color: rgba(255, 255, 255, 0.35);
    }

    &:focus {
      background-color: var(--component-custom-input-bg);
      color: var(--component-custom-input-font-color);
    }
  }
}

.account-switcher-list {
  max-height: 300px;
  overflow-y: auto;
}

// A phone's header: room for every control, and the name kept for screen readers only.
@include media-breakpoint-down(sm) {
  .navbar {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .header__user-icon {
    font-size: 1.25rem;
  }

  .header__user-name {
    @include sr-only;
  }
}

// From sm to md the whole logo fits beside everything else. Below sm it gives way (see _header.scss).
@include media-breakpoint-only(sm) {
  .header__logo {
    flex-shrink: 0;
  }
}

// Every control a 44px target wherever a finger is the pointer, a phone on its side included. The menu
// button is one as a `.btn-icon`.
@include touch-screen {
  // Flex rather than the mixin's inline-flex: a nav link is a block.
  .header__support ::v-deep .nav-link,
  .header__user-menu ::v-deep .dropdown-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    @include touch-target($square: true);
  }
}
</style>
